import React from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import {
  CheckCircleFilled,
  LockOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { APP_NAME } from "../../helpers/constants";
import { APP_PRIVACY_ROUTE, APP_TERMS_ROUTE } from "../../helpers/routeNames";
import Logo from "../../components/general/Logo";

const ApplyThankYouPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const propertyName = location.state?.propertyName || "the property";
  const screening = searchParams.get("screening");
  const isScreeningPaid = screening === "paid";
  const isScreeningCancelled = screening === "cancelled";

  const title = isScreeningPaid
    ? "Screening fee paid"
    : isScreeningCancelled
      ? "Payment cancelled"
      : "Application received";

  return (
    <>
      <Helmet>
        <title>
          {title} | {APP_NAME}
        </title>
      </Helmet>
      <PageShell>
        <BrandBar>
          <BrandBarInner>
            <Logo size="small" color="brand" showTagline={false} linkTo="/" />
            <SecurePill aria-label="Secure application">
              <LockOutlined aria-hidden />
              <SecurePillText>Secure application</SecurePillText>
            </SecurePill>
          </BrandBarInner>
        </BrandBar>

        <ShellInner>
          <SuccessCard>
            <SuccessIcon aria-hidden>
              <CheckCircleFilled />
            </SuccessIcon>
            <Title>{title}</Title>
            {isScreeningPaid ? (
              <>
                <Lead>Your screening fee payment was received.</Lead>
                <NextNote>
                  Check your email for a separate link to complete the
                  background check with the screening provider. You can close
                  this tab after you open that email.
                </NextNote>
                <DoneNote>Watch for the screening email next.</DoneNote>
              </>
            ) : isScreeningCancelled ? (
              <>
                <Lead>No payment was taken.</Lead>
                <NextNote>
                  If you still want to complete screening, use the pay link from
                  your landlord&apos;s email, or ask them to resend it.
                </NextNote>
                <DoneNote>You can close this tab now.</DoneNote>
              </>
            ) : (
              <>
                <Lead>
                  Your application for {propertyName} has been submitted.
                </Lead>
                <NextNote>
                  The property owner will review your application and may ask
                  you to complete a paid background check next.
                </NextNote>
                <DoneNote>You can close this tab now.</DoneNote>
              </>
            )}
          </SuccessCard>
        </ShellInner>

        <PageFoot>
          <PageFootInner>
            <span>
              <SafetyCertificateOutlined aria-hidden /> Information is encrypted
              in transit
            </span>
            <PageFootLinks>
              <PageFootLink
                href={APP_TERMS_ROUTE}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </PageFootLink>
              <PageFootLinkSep aria-hidden>·</PageFootLinkSep>
              <PageFootLink
                href={APP_PRIVACY_ROUTE}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </PageFootLink>
            </PageFootLinks>
            <span>Powered by {APP_NAME}</span>
          </PageFootInner>
        </PageFoot>
      </PageShell>
    </>
  );
};

export default ApplyThankYouPage;

const PageShell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f3f6f1 0%, #f8fbef 42%, #eef4ea 100%);
`;

const BrandBar = styled.header`
  background: #ffffff;
  border-bottom: 1px solid rgba(44, 95, 45, 0.18);
`;

const BrandBarInner = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const SecurePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: rgba(44, 95, 45, 0.08);
  border: 1px solid rgba(44, 95, 45, 0.22);
  color: var(--color-primary-dark, #2c5f2d);
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 375px) {
    gap: 0;
    padding: 0.4rem;
    justify-content: center;
  }
`;

const SecurePillText = styled.span`
  @media (max-width: 375px) {
    display: none;
  }
`;

const ShellInner = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 2.5rem 1rem;
  flex: 1 1 auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const SuccessCard = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.08);
  padding: 2rem 1.5rem 1.75rem;
  text-align: center;
`;

const SuccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: var(--color-primary, #2c5f2d);
  line-height: 1;

  .anticon {
    font-size: 72px !important;
  }

  .anticon svg {
    width: 72px !important;
    height: 72px !important;
  }
`;

const Title = styled.h1`
  margin: 0 0 0.65rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
`;

const Lead = styled.p`
  margin: 0 0 1.25rem;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 400;
  color: #1f2937;
`;

const NextNote = styled.p`
  margin: 0 0 1.5rem;
  padding: 0.9rem 1rem;
  text-align: left;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #1f2937;
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const DoneNote = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.4;
  color: #374151;
`;

const PageFoot = styled.footer`
  margin-top: auto;
  border-top: 1px solid rgba(44, 95, 45, 0.12);
  background: rgba(255, 255, 255, 0.72);
`;

const PageFootInner = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  justify-content: space-between;
  align-items: center;
  color: #4b5563;
  font-size: 0.8125rem;

  .anticon {
    margin-right: 0.35rem;
    color: var(--color-primary, #2c5f2d);
  }

  @media (max-width: 640px) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 0.35rem;
  }
`;

const PageFootLinks = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const PageFootLinkSep = styled.span`
  color: #9ca3af;
`;

const PageFootLink = styled.a`
  color: #4b5563;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-primary-dark, #2c5f2d);
  }
`;
